import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import {
  CtActivateActionsConfiguration,
  CtBinaryOperator,
  CtDatatableActionConfiguration,
  CtDatatableActionCustomConfiguration,
  CtDatatableConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelOperator,
  CtModelService
} from "@ctsolution/ct-framework";
import { ActivatedRoute, Router } from "@angular/router";
import { DEFAULT_DATATABLE_CONFIGURATION } from "../../../_core/constants";
import { CTMGeneralService } from "../../../_core/lib/general.service";
import { WorkOrderRouteData } from "../../../_core/route-data/work-order.route-data";
import { CtManagerService } from "../../../_core/lib/ct-manager.service";
import { CTMWorkOrderViewType } from "../../../_core/enum/work-order-type";
import { WorkOrderService } from "../work-order.service";
import { LangDownloadFileConfiguration, WorkOrderRowsExportTypeVO } from "../../lang-download-file/lang-download-file.configuration";
import { ReportDownloadParameter } from "../../../_core/classes/report-download.parameter";
import { WorkOrderDownloadMenuConfiguration } from "../../work-order-download-menu/work-order-download-menu.configuration";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { CtGenericDialogComponent, CtGenericDialogConfiguration } from "@ctsolution/ct-framework";
import { WorkOrderStatusEnum } from "../../../_core/enum/work-order-state";
import { DataRequest, MethodEnum } from "@ctsolution/ct-webapi";

@Component({
  selector: "app-work-order-list",
  template: `
    <ct-model *ngIf="configuration" [configuration]="configuration"></ct-model>

    <!-- Template per il bottone di download - supporta sia il nuovo componente che quello legacy -->
    <ng-template #downloadPdfButton let-value="value">

      <!-- Nuovo componente con menu avanzato (se enabled = true) -->
      <app-work-order-download-menu
        *ngIf="workOrderDownloadMenuConfiguration && workOrderDownloadMenuConfiguration.enabled"
        [configuration]="workOrderDownloadMenuConfiguration"
        [workOrderOid]="value?.Oid">
      </app-work-order-download-menu>

      <!-- Componente legacy per compatibilità (se enabled = false o configurazione non presente) -->
      <app-lang-download-file
        *ngIf="!workOrderDownloadMenuConfiguration || !workOrderDownloadMenuConfiguration.enabled"
        [configuration]="langDownloadFileConfiguration"
        (onDownload)="downloadPdf(value, $event)">
      </app-lang-download-file>

    </ng-template>
  `
})
export class WorkOrderListComponent implements OnInit {

  configuration: CtModelConfiguration<WorkOrderListComponent> | null = null;

  // Stato del toggle per nascondere le cancellate (utilizzato nella callback del custom toggle)
  private hideCancelled: boolean = true;

  langDownloadFileConfiguration: LangDownloadFileConfiguration = LangDownloadFileConfiguration
    .create()
    .setColor("primary")
    .setIcon("picture_as_pdf")
    .setButtonType('mat-icon-button')
    .setLanguage([
      {
        language: "Italiano",
        code: "it",
        type: "IT",
        icon: "it"
      }
    ]);

  workOrderDownloadMenuConfiguration: WorkOrderDownloadMenuConfiguration = WorkOrderDownloadMenuConfiguration
    .create()
    .setEnabled(false)
    .setMaxDocumentsToShow(5)
    .setColor("primary")
    .setIcon("picture_as_pdf")
    .setButtonType('mat-icon-button')
    .setGeneratedPdfLabel("PDF Generato Automaticamente")
    .setUploadedDocumentsLabel("Documenti Caricati")
    .setShowFileIcon(true)
    .setLanguages([
      {
        language: "Italiano",
        code: "it",
        type: "IT",
        icon: "it"
      }
    ]);

  depositRedirectCTA: CtDatatableActionConfiguration = new CtDatatableActionConfiguration()
    .setColor("primary")
    .setIcon("warehouse");

  CTMWorkOrderType: CTMWorkOrderViewType = CTMWorkOrderViewType.Standard;
  @ViewChild("downloadPdfButton") downloadPdfButton: TemplateRef<any> | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private general: CTMGeneralService,
    private managerService: CtManagerService,
    private workOrderService: WorkOrderService,
    private dialog: MatDialog,
    private ctModelService: CtModelService<any>) {
  }

  async ngOnInit() {
    await this.setup();
  }

  get isQuotation() {
    return this.CTMWorkOrderType === CTMWorkOrderViewType.Quotation;
  }

  /**
   * Callback invocata dal custom toggle del framework quando l'utente clicca sul toggle.
   * Gestisce l'attivazione/disattivazione del filtro per nascondere gli ordini cancellati.
   *
   * IMPORTANTE: Questa callback deve SOLO modificare i filterValues.
   * Il framework si occupa automaticamente di ricaricare i dati dopo questa callback.
   *
   * @param active - Stato del toggle (true = nascondi cancellate, false = mostra tutte)
   */
  private onToggleHideCancelled(active: boolean): void {

    console.log('[WorkOrder] Toggle callback invocata. Stato:', active);

    // Aggiorna lo stato interno
    this.hideCancelled = active;

    // Gestisci il filtro nei filterValues
    if (active) {

      // TOGGLE ON: Nascondi ordini cancellati
      // Aggiungi o aggiorna il filtro per escludere gli ordini con State.Type = Cancelled
      this.configuration!.filterValues.upsertConstantFilter(
        CTModelDatatableFilter.create()
          .setField('State.Type')
          .setValue(WorkOrderStatusEnum.Cancelled)
          .setOperatorType(CtBinaryOperator.NotEqual)
      );

      console.log('[WorkOrder] Filtro "State.Type != Cancelled" aggiunto');

    } else {

      // TOGGLE OFF: Mostra tutti gli ordini (anche cancellati)
      // Rimuovi il filtro sul campo State.Type
      const removed = this.configuration!.filterValues.removeConstantByField('State.Type');

      console.log('[WorkOrder] Filtro "State.Type" rimosso:', removed);

    }

    // NOTA: NON serve chiamare setup() o fetchData()!
    // Il framework chiama automaticamente fetchData() dopo questa callback:
    // - Durante inizializzazione: setupContainer() → callback → fetchData automatica
    // - Al click utente: toggleCustomFilter() → callback → fetchData() automatica
    console.log('[WorkOrder] Filtri aggiornati. FetchData verrà chiamata automaticamente dal framework.');

  }

  /**
   * Gestisce la cancellazione con modale di conferma personalizzata
   */
  private async onDeleteWorkOrder(event: any): Promise<void> {
    // ✅ Verifica se l'ordine è già cancellato
    if (event?.State?.Type === WorkOrderStatusEnum.Cancelled) {
      // Mostra modale informativa
      this.dialog.open(CtGenericDialogComponent, {
        data: {
          configuration: CtGenericDialogConfiguration.create()
            .setTitle('Attenzione')
            .setContent('Risulta già in stato cancellato')
            .setSubmitLabel('Chiudi')
        },
        maxWidth: '500px',
        minWidth: '300px'
      });
      return;
    }

    // Apri la modale di conferma personalizzata
    const dialogRef = this.dialog.open(CtGenericDialogComponent, {
      data: {
        configuration: CtGenericDialogConfiguration.create()
          .setTitle('Conferma cancellazione')
          .setContent('Sei sicuro di voler cambiare lo stato in cancellata?')
          .setCancelLabel('Annulla')
          .setSubmitLabel('Conferma')
      },
      maxWidth: '500px',
      minWidth: '300px'
    });

    // Aspetta la risposta dell'utente
    const confirmed = await dialogRef.afterClosed().toPromise();

    // Se l'utente ha confermato, procedi con la cancellazione
    if (confirmed) {
      // Chiama il servizio del framework con skipConfirm = true
      const result = await this.ctModelService.delete(
        this.configuration!,
        event,
        true // skipConfirm = true
      );

      // Se la cancellazione è andata a buon fine, ricarica la lista
      if (result) {
        result.subscribe({
          next: () => {
            // Ricarica la configurazione
            this.configuration = null;
            setTimeout(async () => {
              await this.setup();
            }, 0);
          },
          error: (error) => {
            console.error('Errore durante la cancellazione:', error);
          }
        });
      }
    }
  }

  async setup() {

    this.CTMWorkOrderType = await this.workOrderService.getRouteWorkOrderType(this.route);

    await this.managerService.getComponentOverride("workOrder", "langDownloadFileConfiguration", this.langDownloadFileConfiguration).then((component) => {
      return;
    });

    await this.managerService.getComponentOverride("workOrder", "workOrderDownloadMenuConfiguration", this.workOrderDownloadMenuConfiguration).then((component) => {
      return;
    });

    const controllerRoute = this.workOrderService.getRoutePathController(this.CTMWorkOrderType);

    // ✅ Ricrea la datatableConfiguration ogni volta
    const datatableConfiguration: CtDatatableConfiguration = DEFAULT_DATATABLE_CONFIGURATION();

    const activateActions = CtActivateActionsConfiguration.create()
      .setDoubleClickCB(event => this.general.navigateOnCTModelRouter(controllerRoute, "edit", {
        value: event,
        configuration: this.configuration!
      }));

    datatableConfiguration.setActivateActionsConfiguration(activateActions);

    const config = CtModelConfiguration
      .create<WorkOrderListComponent>()
      .setRouteData(WorkOrderRouteData())
      .setCTDatatableConfiguration(datatableConfiguration)
      .setOnCreate(() => this.general.navigateOnCTModelRouter(controllerRoute, "create"))
      .setOnEdit(value => this.general.navigateOnCTModelRouter(controllerRoute, "edit", {
        value,
        configuration: this.configuration
      }))
      .setOperations([
        CtModelOperator.Delete,
        CtModelOperator.FilterCustomToggle  // ✅ Abilita il custom toggle per nascondere cancellate
      ])
      .setOnDelete((event: any) => this.onDeleteWorkOrder(event))

      // =================================================================
      // CONFIGURAZIONE FILTER CUSTOM TOGGLE
      // =================================================================
      // Configurazione del custom toggle per nascondere gli ordini cancellati
      .setCustomToggleLabel('Nascondi cancellate')  // Label del toggle
      .setCustomToggleInitialState(true)  // Stato iniziale: nascondi cancellate
      .setCustomTogglePersistState(true)  // Abilita persistenza in sessionStorage (default)
      .setOnCustomToggle((active: boolean) => this.onToggleHideCancelled(active))  // Callback

      .setFieldsCustomTemplates([
        {
          prop: 'Quotation',
          visible: false
        },
        {
          prop: 'QuotationAcceptAt',
          visible: this.isQuotation
        },
        {
          prop: 'StartAt',
          visible: !this.isQuotation
        },
        {
          prop: 'ExpiredAt',
          visible: !this.isQuotation
        },
        {
          prop: 'TotalAmount',
          visible: this.isQuotation
        }
      ]);

    const userHasWarehouseManagementEnabled = await this.managerService.hasEnabled.warehouse() ?? false;

    datatableConfiguration
      .setCustomDatatableActionConfiguration(
        CtDatatableActionCustomConfiguration
          .create()
          .setTemplateOutlet(this.downloadPdfButton))
      .setDeleteActionEnabled(true);

    let constantFilters;

    switch (this.CTMWorkOrderType) {

      case CTMWorkOrderViewType.Quotation:
        constantFilters = new Array<CTModelDatatableFilter>(
          this.getCTModelDatatableFilter<Array<any>>('Quotation = True and QuotationAcceptAt is null', [], CtBinaryOperator.AndOperator));
        break;

      case CTMWorkOrderViewType.Standard:
      default:
        constantFilters = new Array<CTModelDatatableFilter>(
          this.getCTModelDatatableFilter<Array<any>>('(Quotation = True and QuotationAcceptAt is not null) or Quotation = False', [], CtBinaryOperator.AndOperator));
        break;

    }

    constantFilters.forEach(filter => config.filterValues.addConstantFilter(filter));

    // ⚠️ IMPORTANTE: NON aggiungere più il filtro qui manualmente!
    // Il filtro per nascondere le cancellate viene gestito automaticamente dalla callback
    // del custom toggle (onToggleHideCancelled) che viene invocata durante setupContainer()
    // PRIMA della fetch automatica.
    //
    // La callback verrà invocata automaticamente in due momenti:
    // 1. Durante l'inizializzazione (setupContainer) con lo stato salvato o iniziale
    // 2. Al click dell'utente sul toggle
    //
    // In entrambi i casi, la callback modificherà this.configuration.filterValues
    // aggiungendo o rimuovendo il filtro "State.Type != Cancelled"

    this.configuration = config;

    this.route
      .data
      .subscribe(data => {
        if (this.isQuotation) data['title'] = "CT_MENU.quotations";
      });


    if (this.isQuotation && this.configuration) {
      const customFieldsRequest = new DataRequest()
        .setController([WorkOrderRouteData().controller!])
        .setMethod(MethodEnum.GET)
        .setAction('Fields')
        .setQueryParams({
          configurationCode: 'Quotation'
        });

      this.configuration.setGetFieldsDataRequest(customFieldsRequest);
    }
  }

  downloadPdf(value?: { Oid: number | null }, lang?: any) {

    if (!value?.Oid) return;

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setWorkOrderOid(value.Oid)
      .setTemplateLanguage(lang.code ?? 'it')
      .setRowType(lang.rowType ?? WorkOrderRowsExportTypeVO.All)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('WorkOrderPdf')
      .setLabelDownload(`WorkOrder${value.Oid}`);

    this.workOrderService.downloadWorkOrderPDF(parameter);

  }

  private getCTModelDatatableFilter = <T>(field: string, value: T, operator: CtBinaryOperator) => CTModelDatatableFilter
    .create()
    .setField(field)
    .setValue(value).setOperatorType(operator);

}
